import { useCallback } from 'react';

import { MAX_EXPLORE_ROUNDS } from '@/api/v4/genai-request-history.api';
import { modalState } from '@/state/modal.state';
import type { IOption } from '@/types';
import { useAtomValue, useSetAtom } from 'jotai';

import type { ExampleCompany } from '../explore.state';
import { exploreFormState } from '../explore.state';
import { isRegeneratePanelOpenState } from '../regenerate/regenerate-explore.state';

import {
  buildCreateExploreRoundPayload,
  getExploreConflictCode,
} from './build-create-explore-round-payload';
import { useCreateExploreRoundMutation } from './use-create-explore-round.mutation';

export const useSubmitExploreRound = ({
  latestRoundNumber,
  requestId,
  viewingRoundNumber,
}: {
  latestRoundNumber: number;
  requestId: string;
  viewingRoundNumber: number;
}) => {
  const exploreSettings = useAtomValue(exploreFormState);
  const createRoundMutation = useCreateExploreRoundMutation(requestId);
  const setModalState = useSetAtom(modalState);
  const setRegeneratePanelOpen = useSetAtom(isRegeneratePanelOpenState);

  const submitRound = useCallback(
    async ({
      confirmTruncate,
      defaultDescription,
      defaultExampleCompanies,
      defaultLocations,
      onBeforeSubmit,
      reopenPanelOnTruncateConflict,
      updatedDescription,
      updatedExampleCompanies,
      updatedLocations,
    }: {
      confirmTruncate?: boolean;
      defaultDescription: string;
      defaultExampleCompanies: ExampleCompany[];
      defaultLocations: IOption[];
      onBeforeSubmit?: () => void;
      reopenPanelOnTruncateConflict?: boolean;
      updatedDescription: string;
      updatedExampleCompanies: ExampleCompany[];
      updatedLocations: IOption[];
    }) => {
      const payload = buildCreateExploreRoundPayload({
        activeRoundNumber: viewingRoundNumber,
        confirmTruncate,
        defaultDescription,
        defaultExampleCompanies,
        defaultLocations,
        enableExploreAccountData: exploreSettings.enabledExploreAccountData,
        model: exploreSettings.model,
        updatedDescription,
        updatedExampleCompanies,
        updatedLocations,
      });

      if (!payload) {
        return;
      }

      onBeforeSubmit?.();

      try {
        await createRoundMutation.mutateAsync(payload);
      } catch (error) {
        const code = getExploreConflictCode(error);
        if (code === 'ROUND_TRUNCATE_REQUIRED') {
          if (reopenPanelOnTruncateConflict) {
            setRegeneratePanelOpen(true);
          }
          setModalState({
            activeRoundNumber: viewingRoundNumber,
            latestRoundNumber,
            onConfirm: () => {
              void submitRound({
                confirmTruncate: true,
                defaultDescription,
                defaultExampleCompanies,
                defaultLocations,
                onBeforeSubmit,
                reopenPanelOnTruncateConflict,
                updatedDescription,
                updatedExampleCompanies,
                updatedLocations,
              });
            },
            state: 'confirmExploreRoundTruncate',
          });
        }
      }
    },
    [
      createRoundMutation,
      exploreSettings.enabledExploreAccountData,
      exploreSettings.model,
      latestRoundNumber,
      setModalState,
      setRegeneratePanelOpen,
      viewingRoundNumber,
    ],
  );

  const submitWithTruncateGuard = useCallback(
    async (args: {
      defaultDescription: string;
      defaultExampleCompanies: ExampleCompany[];
      defaultLocations: IOption[];
      onBeforeSubmit?: () => void;
      reopenPanelOnTruncateConflict?: boolean;
      updatedDescription: string;
      updatedExampleCompanies: ExampleCompany[];
      updatedLocations: IOption[];
    }) => {
      if (
        viewingRoundNumber >= MAX_EXPLORE_ROUNDS ||
        createRoundMutation.isPending
      ) {
        return;
      }

      if (viewingRoundNumber < latestRoundNumber) {
        setModalState({
          activeRoundNumber: viewingRoundNumber,
          latestRoundNumber,
          onConfirm: () => {
            void submitRound({ ...args, confirmTruncate: true });
          },
          state: 'confirmExploreRoundTruncate',
        });
        return;
      }

      await submitRound(args);
    },
    [
      createRoundMutation.isPending,
      latestRoundNumber,
      setModalState,
      submitRound,
      viewingRoundNumber,
    ],
  );

  return {
    isSubmitting: createRoundMutation.isPending,
    submitWithTruncateGuard,
  };
};
